import dynamic from 'next/dynamic';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Portal from '@/common/Portal';
import GlobalContext from '~/context/vdp/GlobalContext';
import { getSpinnerIsImmersive } from '~/selectors';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import GalleryImages from './GalleryImages';
import { Wrapper } from './LiteGallery.styles';
import SpinnerOverlay from './Spinner/SpinnerOverlay';

const SpinnerLite = dynamic(
  () => import(/* webpackChunkName: "Spinner" */ './Spinner'),
  {
    ssr: false
  }
);

const LiteGalleryFallbackComponent = () => (
  <Wrapper>
    <GalleryImages />
  </Wrapper>
);

const LiteGallery = () => {
  const { openImmersive, spinnerReady } = useContext(GlobalContext);
  const isSpinnerImmersive = useSelector(getSpinnerIsImmersive);
  const [shouldFetchSpinner, setShouldFetchSpinner] = useState(false);
  const [shouldOverlayMount, setShouldOverlayMount] = useState(false);
  const [transitionOverlay, setTransitionOverlay] = useState(false);

  const closeOverlay = useCallback(() => {
    setTimeout(() => {
      setShouldOverlayMount(false);
    }, 500);
    setTransitionOverlay(false);
  }, []);

  const handleOverlay = useCallback(() => {
    if (spinnerReady.isLoading) {
      setShouldOverlayMount(true);
    } else if (isSpinnerImmersive || spinnerReady.hasError) {
      closeOverlay();
    }
  }, [
    closeOverlay,
    isSpinnerImmersive,
    spinnerReady.hasError,
    spinnerReady.isLoading
  ]);

  const cancelSpinnerLoad = useCallback(() => {
    setShouldFetchSpinner(false);
    closeOverlay();
  }, [closeOverlay]);

  const handleOpenSpinner = useCallback(() => {
    setShouldFetchSpinner(true);
    handleOverlay();
    if (spinnerReady.isLoaded) {
      openImmersive();
    }
  }, [handleOverlay, openImmersive, spinnerReady.isLoaded]);

  useEffect(() => {
    if (spinnerReady.isLoaded) {
      openImmersive();
    }
  }, [openImmersive, spinnerReady.isLoaded]);

  useEffect(() => {
    handleOverlay();
  }, [handleOverlay]);

  return (
    <>
      <Portal open={shouldOverlayMount} id="spinner-overlay-loader">
        <SpinnerOverlay
          cancelSpinnerLoad={cancelSpinnerLoad}
          setTransitionOverlay={setTransitionOverlay}
          transitionOverlay={transitionOverlay}
        />
      </Portal>
      <Wrapper>
        <GalleryImages handleOpenSpinner={handleOpenSpinner} />
        {shouldFetchSpinner && <SpinnerLite />}
      </Wrapper>
    </>
  );
};

export default withErrorBoundary(LiteGallery, LiteGalleryFallbackComponent);
